import type {
  ItemAdhesive,
  ItemFinish,
  ItemLaminate,
  ItemMarketingName,
  ItemMaterial,
  ItemOrientation,
  ItemShape,
  ProductPublicDto,
} from "@sticker-giant/sg-frontend-common";
import { usePdpStore } from "@/stores/usePdpStore";
import { isQuantityValid } from "./quantities";

export interface PdpParams {
  itemId?: string;
  specId?: string;
  quantity?: number;
  product?: string;
  material?: string;
  shape?: string;
  laminate?: string;
  finish?: string;
  adhesive?: string;
  orientation?: string;
  height?: string;
  width?: string;
}

const getSearchParamNumber = (value: string | string[] | undefined) => {
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

const getSearchParamString = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) return value[0];
  return value;
};

export const getPdpParamsFromSearchParams = (
  searchParams: Record<string, string | string[] | undefined>,
): PdpParams => ({
  itemId: getSearchParamString(searchParams.itemId),
  specId: getSearchParamString(searchParams.specId),
  quantity: getSearchParamNumber(searchParams.quantity),
  product: getSearchParamString(searchParams.product),
  material: getSearchParamString(searchParams.material),
  shape: getSearchParamString(searchParams.shape),
  laminate: getSearchParamString(searchParams.laminate),
  finish: getSearchParamString(searchParams.finish),
  adhesive: getSearchParamString(searchParams.adhesive),
  orientation: getSearchParamString(searchParams.orientation),
  height: getSearchParamString(searchParams.height),
  width: getSearchParamString(searchParams.width),
});

function validateOption<T extends { sku: string }>(options: T[], value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return options.some(o => o.sku === value) ? value : undefined;
}

function validateDimension(value: string | undefined, min: number, max: number): string | undefined {
  if (value === undefined) return undefined;
  const num = Number(value);
  return !Number.isNaN(num) && num >= min && num <= max ? value : undefined;
}

function validateQuantity(
  quantity: number | undefined,
  product: ProductPublicDto,
  material: ItemMaterial | undefined,
): number | undefined {
  if (quantity === undefined) return undefined;
  return isQuantityValid(product, quantity, material) ? quantity : undefined;
}

export function validateParams(params: PdpParams, product: ProductPublicDto): PdpParams {
  const material = validateOption(product.materialOptions, params.material);
  const resolvedMaterial = (material ?? product.defaultMaterial) as ItemMaterial | undefined;
  const materialOption = product.materialOptions.find(m => m.sku === resolvedMaterial);

  return {
    itemId: params.itemId,
    specId: params.specId,
    product: params.product,
    material,
    shape: validateOption(product.shapeOptions, params.shape),
    laminate: validateOption(materialOption?.laminateOptions ?? product.laminateOptions, params.laminate),
    finish: validateOption(product.finishingOptions, params.finish),
    adhesive: validateOption(materialOption?.adhesiveOptions ?? product.adhesiveOptions, params.adhesive),
    orientation: validateOption(product.orientationOptions, params.orientation),
    height: product.allowCustomSize
      ? validateDimension(
          params.height,
          materialOption?.minHeight ?? product.minCustomHeight,
          materialOption?.maxHeight ?? product.maxCustomHeight,
        )
      : undefined,
    width: product.allowCustomSize
      ? validateDimension(
          params.width,
          materialOption?.minWidth ?? product.minCustomWidth,
          materialOption?.maxWidth ?? product.maxCustomWidth,
        )
      : undefined,
    quantity: validateQuantity(params.quantity, product, resolvedMaterial),
  };
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: simple sequential field assignments
export function hydrateStoreFromUrlParams(params: PdpParams, product?: ProductPublicDto) {
  const validated = product ? validateParams(params, product) : params;
  const store = usePdpStore.getState();

  if (validated.product) store.selectProduct(validated.product as ItemMarketingName);
  if (validated.material) store.setMaterial(validated.material as ItemMaterial);
  if (validated.shape) store.setShape(validated.shape as ItemShape);
  if (validated.laminate) store.setLaminate(validated.laminate as ItemLaminate);
  if (validated.finish) store.setFinish(validated.finish as ItemFinish);
  if (validated.adhesive) store.setAdhesive(validated.adhesive as ItemAdhesive);
  if (validated.orientation) store.setOrientation(validated.orientation as ItemOrientation);
  if (validated.height) {
    store.setHeight(validated.height);
  } else if (typeof product?.defaultHeight === "number") {
    store.setHeight(String(product.defaultHeight));
  }
  if (validated.width) {
    store.setWidth(validated.width);
  } else if (typeof product?.defaultWidth === "number") {
    store.setWidth(String(product.defaultWidth));
  }
  if (validated.quantity) store.setQuantity(validated.quantity);
}
